import React from 'react';
import { Box, Card, CardContent, Chip, TextField, Typography } from '@mui/material';
import { AttachMoney } from '@mui/icons-material';
import VoverPlansSelector from '../VoverPlansSelector';
import type { CapacityPack } from './pricingCalculatorTypes';

interface GestaoCapacitySelectorProps {
  gestaoCapacityPacks: CapacityPack[];
  clientCount: number;
  onClientCountChange: (count: number) => void;
  minGestaoCapacity: number;
  calculateClientCapacityCost: (count: number) => number;
  formatCurrency: (value: number) => string;
}

/**
 * Seletor de capacidade de condomínios do plano Gestão (VCondo Planos: timeline + tabela
 * comparativa, com opção de quantidade personalizada). Extraído de UnifiedPricingCalculator.tsx (F7).
 */
export const GestaoCapacitySelector: React.FC<GestaoCapacitySelectorProps> = ({
  gestaoCapacityPacks,
  clientCount,
  onClientCountChange,
  minGestaoCapacity,
  calculateClientCapacityCost,
  formatCurrency,
}) => {
  if (gestaoCapacityPacks.length === 0) return null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AttachMoney sx={{ mr: 1, color: 'primary.main' }} />
          VCondo Planos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecione o plano de acordo com a capacidade de condomínios da sua operação.
        </Typography>
        <VoverPlansSelector
          packs={gestaoCapacityPacks}
          selectedCapacity={clientCount}
          onSelectCapacity={onClientCountChange}
          recommendedIndex={Math.max(
            0,
            gestaoCapacityPacks.findIndex((p) => Boolean(p.highlight))
          )}
        />
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Precisa de mais condomínios que o plano Enterprise?
          </Typography>
          <TextField
            id="calculator-custom-clients-input"
            type="number"
            size="small"
            label="Outra quantidade"
            value={
              clientCount > 0 && !gestaoCapacityPacks.some((p) => p.capacity === clientCount)
                ? clientCount
                : ''
            }
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (isNaN(v) || e.target.value === '') {
                onClientCountChange(0);
                return;
              }
              onClientCountChange(Math.max(minGestaoCapacity, v));
            }}
            inputProps={{ min: minGestaoCapacity, style: { width: '80px' } }}
            helperText={`Mínimo: ${minGestaoCapacity} condomínios (menor plano)`}
          />
          {clientCount >= minGestaoCapacity && !gestaoCapacityPacks.some((p) => p.capacity === clientCount) && (
            <Chip
              label={`${formatCurrency(calculateClientCapacityCost(clientCount))}/mês`}
              size="small"
              color="info"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default GestaoCapacitySelector;
